import { Box } from "@chakra-ui/react";
import { TooltipWithBounds, defaultStyles } from "@visx/tooltip";
import { isValidElement, ReactElement, ReactNode } from "react";
import { useChartColors } from "utils";

type BasicTooltipPropsWithRenderer<T> = {
  tooltipRenderer: (props: { textColor: string; tooltipData: T }) => ReactNode;
};

type BasicTooltipProps<T> = {
  tooltipTop: number;
  tooltipLeft: number;
  tooltipData: T;
} & (T extends ReactElement
  ? NonNullable<unknown>
  : BasicTooltipPropsWithRenderer<T>);

const BasicTooltip = <T,>({
  tooltipLeft,
  tooltipTop,
  tooltipData,
  ...props
}: BasicTooltipProps<T>) => {
  const { labelColor, tooltipBackground, tooltipBorderColor } =
    useChartColors();

  return (
    <TooltipWithBounds
      top={tooltipTop}
      left={tooltipLeft}
      style={{
        ...defaultStyles,
        background: tooltipBackground,
        borderRadius: "var(--chakra-radii-md)",
        border: `1px solid ${String(tooltipBorderColor)}`,
        boxShadow: "var(--chakra-shadows-lg)",
        zIndex: 10000,
      }}
    >
      <Box p={1} lineHeight={1.4}>
        {isValidElement(tooltipData) ? (
          <Box color={labelColor}>{tooltipData}</Box>
        ) : (
          (
            props as unknown as BasicTooltipPropsWithRenderer<T>
          ).tooltipRenderer({
            textColor: labelColor,
            tooltipData,
          })
        )}
      </Box>
    </TooltipWithBounds>
  );
};

export { BasicTooltip };
